import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import PostSubscribeFormAction from '~/components/Actions/PostSubscribeFormAction';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import Section from '~/components/Section';
import Typography from '~/components/Typography';

import useBillingSummary from '~/hooks/useLoaders/useBillingSummary';

import LoginAction from '~/components/Actions/LoginAction';
import DowngradeAction from '../_actions/DowngradeAction';
import ResubscribeAction from '../_actions/ResubscribeAction';
import UnsubscribeAction from '../_actions/UnsubscribeAction';
import { useAccountActionContext } from '../_context/useAccountActionContext';
import useDowngradeDescription from '../_hooks/useDowngradeDescription';

const Downgrade = () => {
  const { t } = useTranslation('', { keyPrefix: 'account_actions.downgrade' });
  const { publication, email, isInactive, canUnsubscribe, canDowngrade, isLoggedIn } = useAccountActionContext();
  const { isLoading, billingSummary } = useBillingSummary({
    enabled: isLoggedIn,
  });
  const isPendingDowngrade = billingSummary?.pending_downgrade || false;
  const isProratedDowngradeAvailable = billingSummary?.prorated_downgrade_available || false;

  const [postUnsubscribeFormId, setPostUnsubscribeFormId] = useState<null | string>(null);

  const description = useDowngradeDescription({
    isPendingDowngrade,
    isProratedDowngradeAvailable,
  });

  const handleSetPostUnsubscribeFormId = (id: string) => {
    setPostUnsubscribeFormId(id);
  };

  if (postUnsubscribeFormId) {
    return (
      <PostSubscribeFormAction
        publication={publication}
        formId={postUnsubscribeFormId}
        email={email}
        onClose={() => setPostUnsubscribeFormId(null)}
      />
    );
  }

  return (
    <Section title={t('title')} description={isInactive ? '' : description} className="relative overflow-hidden">
      <div className="flex space-x-6" id="account-actions">
        {canDowngrade && !isPendingDowngrade && <DowngradeAction />}
        {isInactive && <ResubscribeAction />}
        {!isInactive && (
          <div>
            {canUnsubscribe ? (
              <UnsubscribeAction publication={publication} onUnsubscribe={handleSetPostUnsubscribeFormId} />
            ) : (
              <LoginAction allowSignup={false} hash="account-actions" />
            )}
          </div>
        )}
      </div>

      {isLoading && (
        <div className="absolute inset-0 flex h-full w-full items-center justify-center bg-white bg-opacity-90">
          <LoadingSpinner className="mr-3" />
          <Typography as="p" size="sm" className="text-gray-700">
            {t('loading')}…
          </Typography>
        </div>
      )}
    </Section>
  );
};

export default Downgrade;
